/**
 * SubscriptionPage - Page de gestion d'abonnement complète
 * Onglets : Abonnement, Factures, Moyens de paiement, Plans, Informations de facturation
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, FileText, Receipt, Building2, Loader,
  Check, X, Download, Plus, Edit2, AlertCircle,
  ExternalLink
} from 'lucide-react';
import config from '../config';

const PLANS = {
  SOLO: {
    name: 'Solo',
    description: 'Pour les freelances et consultants',
    seats: 1,
    accounts: 5,
    monthly: 19,
    annual: 15,
    addons: { seat: 19, account: 5 },
    features: ['1 utilisateur', 'Tous les connecteurs', '5 comptes', 'Clients illimités', 'Serveur MCP']
  },
  TEAM: {
    name: 'Team',
    description: 'Pour les petites équipes',
    seats: 3,
    accounts: 25,
    monthly: 69,
    annual: 57,
    addons: { seat: 15, account: 4 },
    popular: true,
    features: ['3 utilisateurs', 'Tous les connecteurs', '25 comptes', 'Clients illimités', 'Serveur MCP', 'Collaboration équipe']
  },
  AGENCY: {
    name: 'Agency',
    description: 'Pour les agences marketing',
    seats: 10,
    accounts: 100,
    monthly: 179,
    annual: 149,
    addons: { seat: 12, account: 3 },
    features: ['10 utilisateurs', 'Tous les connecteurs', '100 comptes', 'Clients illimités', 'Serveur MCP', 'Collaboration équipe', 'Support prioritaire']
  },
  // Plan legacy
  PRO: {
    name: 'Pro',
    description: 'Plan Pro (legacy)',
    seats: 3,
    accounts: 25,
    monthly: 9.99,
    annual: null,
    legacy: true,
    features: ['3 utilisateurs', '25 comptes', 'Fonctionnalités standard']
  }
};

// Animation de chargement
const spinAnimation = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`;

// Fonction pour obtenir le plan normalisé
const getNormalizedPlan = (plan) => {
  return plan || null;
};

/**
 * Affiche la limite effective renvoyée par l'API (elle inclut les extras achetés et
 * les quotas sur-mesure éventuels). Retombe sur la valeur du plan si l'API n'a rien renvoyé.
 */
const formatLimit = (apiTotal, planValue, extra) => {
  const total = apiTotal !== undefined && apiTotal !== null
    ? apiTotal
    : (planValue === -1 ? -1 : planValue + (extra || 0));
  return total === -1 ? '∞' : total;
};

// Composant TabButton (même style que OrganizationView)
const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '10px 16px',
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: active ? '2px solid #1a1a1a' : '2px solid transparent',
      color: active ? '#1a1a1a' : '#6b7280',
      fontWeight: active ? '600' : '400',
      cursor: 'pointer',
      marginBottom: '-1px',
      fontSize: '14px'
    }}
  >
    {children}
  </button>
);

// Composant AlertMessage
const AlertMessage = ({ type, message, onClose }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    background: type === 'error' ? '#fef2f2' : '#f0fdf4',
    color: type === 'error' ? '#dc2626' : '#16a34a',
    border: `1px solid ${type === 'error' ? '#fecaca' : '#bbf7d0'}`
  }}>
    {type === 'error' ? <AlertCircle size={18} /> : <Check size={18} />}
    {message}
    <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
      <X size={16} />
    </button>
  </div>
);

/**
 * Ce que chaque statut d'abonnement veut dire, en clair.
 *
 * `INACTIVE` est le piège : il ne signifie pas « suspendu » mais « paiement ouvert, jamais
 * confirmé » — l'état dans lequel on reste quand on renonce devant la page Stripe. Le ranger
 * dans un `else` qui affichait « Actif » donnait à lire l'inverse de la réalité.
 */
const ETAT_LIBELLE = {
  TRIALING: 'Essai gratuit',
  ACTIVE: 'Actif',
  INACTIVE: 'Paiement non finalisé',
  PAST_DUE: 'Paiement en retard',
  UNPAID: 'Impayé',
  CANCELED: 'Annulé'
};

/** La couleur du badge : seuls un essai et un abonnement en règle sont rassurants. */
const ETAT_BADGE = {
  TRIALING: 'trialing',
  ACTIVE: 'active',
  INACTIVE: 'canceled',
  PAST_DUE: 'canceled',
  UNPAID: 'canceled',
  CANCELED: 'canceled'
};

function SubscriptionPage({ token, user, currentSubscription, activeTab: externalTab, onBack }) {
  /**
   * L'onglet affiché vient de l'extérieur (le fil d'Ariane d'`App.js`), avec « Abonnement »
   * pour défaut. Il n'est plus changé depuis l'intérieur de cette page : les boutons font
   * défiler vers les formules, désormais sur le même écran que l'abonnement.
   */
  const activeTab = externalTab || 'subscription';
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [limits, setLimits] = useState(null);
  // Moyens de paiement et coordonnées, lus chez Stripe (GET /api/subscription/billing).
  const [billing, setBilling] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  // Charger les données d'abonnement
  const loadSubscriptionData = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, invoicesRes, limitsRes, billingRes] = await Promise.all([
        fetch(`${config.API_URL}/api/subscription/current`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${config.API_URL}/api/subscription/invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${config.API_URL}/api/subscription/limits`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${config.API_URL}/api/subscription/billing`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const subData = await subRes.json();
      const invoicesData = await invoicesRes.json();
      const limitsData = await limitsRes.json();
      const billingData = await billingRes.json();

      if (subData.success) {
        setSubscription(subData.subscription);
      }
      if (invoicesData.success) {
        setInvoices(invoicesData.invoices || []);
      }
      if (limitsData.success) {
        setLimits(limitsData);
      }
      if (billingData.success) {
        setBilling(billingData);
      }
    } catch (err) {
      console.error('Erreur chargement abonnement:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSubscriptionData();
  }, [loadSubscriptionData]);

  // Annuler l'abonnement
  const handleCancelSubscription = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler votre abonnement ? Il restera actif jusqu\'à la fin de la période en cours.')) return;

    setActionLoading('cancel');
    try {
      const res = await fetch(`${config.API_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('Abonnement programmé pour annulation');
        loadSubscriptionData();
      } else {
        setError(data.error || 'Erreur lors de l\'annulation');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  // Réactiver l'abonnement
  const handleReactivateSubscription = async () => {
    setActionLoading('reactivate');
    try {
      const res = await fetch(`${config.API_URL}/api/subscription/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('Abonnement réactivé avec succès');
        loadSubscriptionData();
      } else {
        setError(data.error || 'Erreur lors de la réactivation');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  // Changer de plan
  const handleChangePlan = async (planKey) => {
    setActionLoading(planKey);
    try {
      const res = await fetch(`${config.API_URL}/api/subscription/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan: planKey, billingCycle })
      });
      const data = await res.json();
      // `sessionUrl` est le nom que renvoie le serveur (`subscriptionService.createSubscription`).
      // On lisait `checkoutUrl`, qui n'a jamais existé : la redirection vers la page de paiement
      // n'avait donc jamais lieu, et l'écran affichait une erreur alors que la session Stripe
      // venait bien d'être créée. `checkoutUrl` reste accepté au cas où le serveur le renomme.
      const urlDePaiement = data.sessionUrl || data.checkoutUrl;
      if (data.success && urlDePaiement) {
        window.location.href = urlDePaiement;
      } else {
        setError(data.error || 'Erreur lors du changement de plan');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * L'état de l'abonnement, dérivé une fois pour tout l'écran.
   *
   * Ces valeurs étaient calculées dans le bloc d'état ; elles servent désormais à la carte de
   * la formule en cours, qui porte cet état. Les dériver ici évite qu'un même fait soit calculé
   * de deux façons à deux endroits. Les capacités, elles, sont lues sur chaque carte, qui
   * connaît sa propre formule.
   */
  const isTrialing = subscription?.status === 'TRIALING';
  const isCanceled = subscription?.cancelAtPeriodEnd;

  /**
   * L'essai ouvert à la création du compte, sans carte bancaire.
   *
   * Il n'existe que chez nous : aucun abonnement Stripe ne lui correspond, donc ni période de
   * facturation, ni échéance, ni rien à annuler. La distinction se lit sur
   * `stripeSubscriptionId` — un essai Stripe, souscrit avec une carte, garde l'affichage qui
   * lui convient.
   */
  const essaiSansCarte = isTrialing && !subscription?.stripeSubscriptionId;
  const finEssai = subscription?.trialEndsAt ? new Date(subscription.trialEndsAt) : null;
  const joursRestants = finEssai
    ? Math.max(0, Math.ceil((finEssai.getTime() - Date.now()) / 86400000))
    : null;

  /**
   * Ouvrir le portail de facturation Stripe.
   *
   * Le lien ne peut pas être écrit en dur : Stripe le crée à la demande, pour un client donné
   * et pour un seul usage. C'est ce que faisaient les deux onglets, avec l'adresse d'exemple de
   * la documentation — ils ne menaient donc nulle part.
   */
  const [portailEnCours, setPortailEnCours] = useState(false);

  const ouvrirPortailStripe = async () => {
    setPortailEnCours(true);
    setError('');
    try {
      const res = await fetch(`${config.API_URL}/api/subscription/portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || "Impossible d'ouvrir le portail de facturation");
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setPortailEnCours(false);
    }
  };

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Formater le montant
  const formatAmount = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount / 100);
  };

  // Styles
  const styles = {
    card: {
      backgroundColor: '#FAF9F5',
      border: '1px solid #e1e8ed',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '20px'
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#374151',
      margin: '0 0 16px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '32px'
    },
    label: {
      fontSize: '13px',
      color: '#6b7280',
      marginBottom: '4px'
    },
    value: {
      fontSize: '15px',
      fontWeight: '500',
      color: '#374151'
    },
    button: (variant = 'primary') => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      background: variant === 'primary' ? '#1a1a1a' : variant === 'danger' ? '#ef4444' : 'white',
      color: variant === 'primary' || variant === 'danger' ? 'white' : '#374151',
      border: variant === 'secondary' ? '1px solid #d1d5db' : 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }),
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '12px 16px',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '13px',
      fontWeight: '600',
      color: '#6b7280'
    },
    td: {
      padding: '16px',
      borderBottom: '1px solid #f3f4f6',
      fontSize: '14px',
      color: '#374151'
    },
    badge: (status) => ({
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      background: status === 'paid' || status === 'active' ? '#dcfce7' : status === 'canceled' ? '#fee2e2' : '#fef3c7',
      color: status === 'paid' || status === 'active' ? '#15803d' : status === 'canceled' ? '#dc2626' : '#b45309'
    }),
    planCard: (isCurrentPlan) => ({
      border: isCurrentPlan ? '2px solid #1a1a1a' : '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '24px',
      background: isCurrentPlan ? '#fafafa' : 'white',
      position: 'relative'
    }),
    toggle: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    toggleSwitch: (isAnnual) => ({
      width: '48px',
      height: '24px',
      background: isAnnual ? '#1a1a1a' : '#d1d5db',
      borderRadius: '12px',
      position: 'relative',
      cursor: 'pointer',
      transition: 'background 0.3s'
    }),
    toggleKnob: (isAnnual) => ({
      width: '20px',
      height: '20px',
      background: 'white',
      borderRadius: '50%',
      position: 'absolute',
      top: '2px',
      left: isAnnual ? '26px' : '2px',
      transition: 'left 0.3s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
    })
  };

  // Onglet Abonnement
  /**
   * L'onglet Abonnement : la grille des formules, et rien d'autre.
   *
   * ## Pourquoi il n'y a plus de carte d'état
   *
   * L'écran a tenu successivement trois blocs, puis deux. Il n'en reste qu'un, parce que la
   * carte de la formule en cours pouvait porter tout ce que la carte d'état disait : son nom,
   * son statut, son échéance, ses quotas. Les séparer obligeait à lire deux fois la même
   * formule — une fois pour savoir où l'on en est, une fois pour la comparer aux autres — avec
   * le risque permanent que les deux divergent.
   *
   * Ce qui n'appartient qu'à la formule souscrite vit donc **dans sa carte** : l'état, la date
   * de fin d'essai, l'usage des sièges et des comptes, les suppléments, et l'action de rupture.
   * Les autres cartes restent ce qu'elles étaient — un prix et des capacités.
   */
  const renderSubscriptionTab = () => (
    <div>
      {!subscription && (
        <div style={{ ...styles.card, textAlign: 'center', padding: '32px' }}>
          <CreditCard size={40} style={{ color: '#9ca3af', marginBottom: '12px' }} />
          <h4 style={{ margin: '0 0 8px', color: '#374151' }}>Aucun abonnement actif</h4>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Choisissez une formule ci-dessous pour débloquer toutes les fonctionnalités
          </p>
        </div>
      )}

      {renderPlansTab()}
    </div>
  );

  // Onglet Factures
  const renderInvoicesTab = () => (
    <div>
      {invoices.length === 0 ? (
        <div style={{ ...styles.card, textAlign: 'center', padding: '48px' }}>
          <Receipt size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
          <p style={{ margin: 0, color: '#6b7280' }}>Aucune facture pour le moment</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Numéro</th>
                <th style={styles.th}>Montant</th>
                <th style={styles.th}>Statut</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td style={styles.td}>{formatDate(invoice.createdAt)}</td>
                  <td style={styles.td}>{invoice.numero || invoice.stripeInvoiceId || '-'}</td>
                  <td style={styles.td}>{formatAmount(invoice.amount)}</td>
                  <td style={styles.td}>
                    <span style={styles.badge(invoice.status?.toLowerCase())}>
                      {invoice.status === 'PAID' ? 'Payée' : invoice.status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {(invoice.pdfUrl || invoice.invoiceUrl) && (
                      <a
                        href={invoice.pdfUrl || invoice.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ ...styles.button('primary'), padding: '6px 12px', textDecoration: 'none' }}
                      >
                        <Download size={14} />
                        Télécharger
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Onglet Moyens de paiement
  /** Le dossier Stripe n'existe qu'à partir de la première souscription. */
  const renderDossierAbsent = (quoi) => (
    <div style={{ ...styles.card, textAlign: 'center', padding: '40px' }}>
      <CreditCard size={40} style={{ color: '#9ca3af', marginBottom: '12px' }} />
      <p style={{ margin: 0, color: '#6b7280' }}>
        {quoi} apparaîtront ici dès votre première souscription.
      </p>
    </div>
  );

  /** Le bouton qui mène au portail Stripe, seul endroit où ces données se modifient. */
  const boutonPortail = (libelle, Icone) => (
    <button style={styles.button('primary')} onClick={ouvrirPortailStripe} disabled={portailEnCours}>
      {portailEnCours ? <Loader size={16} className="spin" /> : <Icone size={16} />}
      {libelle}
    </button>
  );

  /**
   * Onglet Moyens de paiement.
   *
   * Les cartes sont **affichées** ici et **modifiées** dans le portail Stripe. C'est la bonne
   * répartition : savoir quelle carte est enregistrée se consulte souvent, la changer rarement —
   * et surtout, aucune donnée de carte ne doit transiter par nous. Stripe n'expose que la
   * marque, les quatre derniers chiffres et l'échéance ; le numéro complet n'est accessible à
   * personne.
   */
  const renderPaymentTab = () => {
    if (!billing?.dossierOuvert) return renderDossierAbsent('Vos moyens de paiement');

    const cartes = billing.moyensDePaiement || [];

    return (
      <div>
        {cartes.length === 0 ? (
          <div style={{ ...styles.card, textAlign: 'center', padding: '40px' }}>
            <CreditCard size={40} style={{ color: '#9ca3af', marginBottom: '12px' }} />
            <p style={{ margin: '0 0 20px', color: '#6b7280' }}>
              Aucune carte enregistrée. Votre essai n'en demande pas.
            </p>
            {boutonPortail('Ajouter une carte', Plus)}
          </div>
        ) : (
          <div style={styles.card}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              {cartes.map((c) => (
                <div key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '14px 16px', border: '1px solid #e5e7eb', borderRadius: '8px'
                }}>
                  <CreditCard size={20} style={{ color: '#6b7280' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '15px', color: '#374151', fontWeight: 500 }}>
                      {(c.marque || 'Carte').replace(/^./, (l) => l.toUpperCase())} •••• {c.quatreDerniers || '????'}
                    </div>
                    {c.moisExpiration && (
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                        Expire le {String(c.moisExpiration).padStart(2, '0')}/{c.anneeExpiration}
                      </div>
                    )}
                  </div>
                  {c.parDefaut && <span style={styles.badge('active')}>Utilisée par défaut</span>}
                </div>
              ))}
            </div>
            {boutonPortail('Gérer mes moyens de paiement', ExternalLink)}
          </div>
        )}
      </div>
    );
  };

  /**
   * Onglet Facturation : les coordonnées qui figureront sur les factures.
   *
   * Même répartition que pour les cartes — lues ici, modifiées dans le portail. Les champs vides
   * sont **affichés comme tels** plutôt que masqués : une adresse manquante est précisément ce
   * qu'on vient vérifier avant d'émettre une facture.
   */
  const renderBillingTab = () => {
    if (!billing?.dossierOuvert) return renderDossierAbsent('Vos informations de facturation');

    const f = billing.facturation || {};
    const a = f.adresse || {};
    const adresse = [a.line1, a.line2, [a.postal_code, a.city].filter(Boolean).join(' '), a.country]
      .filter(Boolean)
      .join(', ');

    const ligne = (libelle, valeur) => (
      <div style={{ display: 'flex', gap: '16px', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
        <div style={{ ...styles.label, width: '180px', flexShrink: 0 }}>{libelle}</div>
        <div style={{ fontSize: '15px', color: valeur ? '#374151' : '#9ca3af' }}>
          {valeur || 'Non renseigné'}
        </div>
      </div>
    );

    return (
      <div>
        <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '15px' }}>
          Ces informations figurent sur vos factures.
        </p>

        <div style={styles.card}>
          {ligne('Nom ou raison sociale', f.nom)}
          {ligne('Email de facturation', f.email)}
          {ligne('Adresse', adresse)}
          {ligne('Numéro de TVA', (f.numerosTva || []).map((t) => t.valeur).join(', '))}

          <div style={{ marginTop: '20px' }}>
            {boutonPortail('Modifier mes informations', Edit2)}
          </div>
        </div>
      </div>
    );
  };

  const renderPlansTab = () => {
    // Utiliser subscription chargé ou currentSubscription en fallback, avec mapping legacy
    const rawPlan = subscription?.plan || currentSubscription?.plan;
    const currentPlan = getNormalizedPlan(rawPlan);
    const isAnnual = billingCycle === 'annual';

    return (
      <div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: '16px', flexWrap: 'wrap', marginBottom: '24px'
        }}>
          {/* Ce qu'il advient en cliquant « Sélectionner » : la question qu'on se pose juste
              avant, et à laquelle l'écran ne répondait nulle part. */}
          <span style={{ fontSize: '15px', color: '#6b7280' }}>
            {essaiSansCarte
              ? "Choisir une formule ne met pas fin à votre essai : rien n'est prélevé avant son terme."
              : subscription
                ? 'Le changement prend effet immédiatement, au prorata de la période en cours.'
                : ''}
          </span>
          <div style={styles.toggle}>
            <span style={{ color: !isAnnual ? '#374151' : '#9ca3af', fontWeight: !isAnnual ? '600' : '400', fontSize: '14px' }}>Mensuel</span>
            <div style={styles.toggleSwitch(isAnnual)} onClick={() => setBillingCycle(isAnnual ? 'monthly' : 'annual')}>
              <div style={styles.toggleKnob(isAnnual)} />
            </div>
            <span style={{ color: isAnnual ? '#374151' : '#9ca3af', fontWeight: isAnnual ? '600' : '400', fontSize: '14px' }}>
              Annuel
              <span style={{ marginLeft: '8px', background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>-17%</span>
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginTop: '16px' }}>
          {Object.entries(PLANS).filter(([key, plan]) => !plan.legacy).map(([key, plan]) => {
            const isCurrentPlan = currentPlan === key;
            const price = isAnnual ? plan.annual : plan.monthly;

            return (
              <div key={key} style={styles.planCard(isCurrentPlan)}>
                {isCurrentPlan && (
                  <span style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '16px',
                    background: '#1a1a1a',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    Plan actuel
                  </span>
                )}

                <h4 style={{ margin: '0 0 8px', fontSize: '18px', color: '#374151' }}>
                  {plan.name}
                </h4>

                <div style={{ marginBottom: '16px' }}>
                  {price ? (
                    <span style={{ fontSize: '28px', fontWeight: '700', color: '#374151' }}>
                      {price}€
                      <span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>/mois</span>
                    </span>
                  ) : (
                    <span style={{ fontSize: '20px', fontWeight: '600', color: '#374151' }}>Sur devis</span>
                  )}
                </div>

                {/*
                  * La formule en cours porte son propre état : statut, échéance, quotas,
                  * suppléments. C'est ce qui a permis de supprimer la carte d'état en haut de
                  * page — la même formule y était décrite une seconde fois.
                  */}
                {isCurrentPlan && subscription && (
                  <div style={{
                    marginBottom: '16px', paddingBottom: '16px',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      {isCanceled ? (
                        <span style={styles.badge('canceled')}>Annulation programmée</span>
                      ) : (
                        <span style={styles.badge(ETAT_BADGE[subscription.status] || 'canceled')}>
                          {ETAT_LIBELLE[subscription.status] || subscription.status}
                        </span>
                      )}
                    </div>

                    {essaiSansCarte ? (
                      <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6 }}>
                        Jusqu'au <strong style={{ color: '#374151' }}>{finEssai ? formatDate(finEssai) : '—'}</strong>
                        {joursRestants !== null && (
                          <> · {joursRestants === 0 ? 'dernier jour' : `${joursRestants} jour${joursRestants > 1 ? 's' : ''} restant${joursRestants > 1 ? 's' : ''}`}</>
                        )}
                        <div>Aucun paiement enregistré.</div>
                      </div>
                    ) : subscription.currentPeriodEnd ? (
                      <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6 }}>
                        Prochaine facture le <strong style={{ color: '#374151' }}>{formatDate(subscription.currentPeriodEnd)}</strong>
                      </div>
                    ) : null}

                    {/* Les quotas consommés : ils ne veulent rien dire sur les autres cartes,
                        où les mêmes nombres sont une promesse et non un relevé. */}
                    <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                      <div>
                        {/* « Utilisateurs », et non « Membres » : la carte dit déjà
                            « 1 utilisateur » dans ses capacités et « +19€/utilisateur » dans ses
                            suppléments. Trois mots pour une même chose obligent à se demander
                            s'ils désignent bien la même. */}
                        <div style={styles.label}>Utilisateurs</div>
                        <div style={{ ...styles.value, fontSize: '15px' }}>
                          {limits?.seats?.current || 0} / {formatLimit(limits?.seats?.total, plan.seats, subscription?.extraSeats)}
                        </div>
                      </div>
                      <div>
                        <div style={styles.label}>Comptes</div>
                        <div style={{ ...styles.value, fontSize: '15px' }}>
                          {limits?.accounts?.current || 0} / {formatLimit(limits?.accounts?.total, plan.accounts, subscription?.extraAccounts)}
                        </div>
                      </div>
                    </div>

                    {(subscription.extraSeats > 0 || subscription.extraAccounts > 0) && (
                      <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280' }}>
                        {subscription.extraSeats > 0 && <div>+{subscription.extraSeats} siège(s) supplémentaire(s)</div>}
                        {subscription.extraAccounts > 0 && <div>+{subscription.extraAccounts} compte(s) supplémentaire(s)</div>}
                      </div>
                    )}
                  </div>
                )}

                {isCurrentPlan ? (
                  /* Rompre, ou revenir sur une rupture — la seule action propre à cette carte.
                     Pendant un essai sans carte, il n'y a rien à rompre : le repère « Plan
                     actuel » suffit, et il est déjà affiché en haut de la carte. */
                  essaiSansCarte ? null : isCanceled ? (
                    <button
                      style={{ ...styles.button('secondary'), width: '100%', justifyContent: 'center' }}
                      onClick={handleReactivateSubscription}
                      disabled={actionLoading === 'reactivate'}
                    >
                      {actionLoading === 'reactivate' ? <Loader size={16} className="spin" /> : null}
                      Réactiver
                    </button>
                  ) : (
                    <button
                      style={{ ...styles.button('danger'), width: '100%', justifyContent: 'center' }}
                      onClick={handleCancelSubscription}
                      disabled={actionLoading === 'cancel'}
                    >
                      {actionLoading === 'cancel' ? <Loader size={16} className="spin" /> : null}
                      Annuler l'abonnement
                    </button>
                  )
                ) : (
                  <button
                    style={{ ...styles.button('primary'), width: '100%', justifyContent: 'center' }}
                    onClick={() => handleChangePlan(key)}
                    disabled={actionLoading === key}
                  >
                    {actionLoading === key ? <Loader size={16} className="spin" /> : null}
                    Sélectionner
                  </button>
                )}

                <ul style={{ margin: '20px 0 0', padding: 0, listStyle: 'none' }}>
                  {plan.features.map((feature, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', fontSize: '13px', color: '#6b7280' }}>
                      <Check size={14} style={{ color: '#10b981' }} />
                      {feature}
                    </li>
                  ))}
                </ul>

                {plan.addons && (
                  <div style={{ marginTop: '12px', padding: '8px 12px', background: '#f3f4f6', borderRadius: '6px', fontSize: '11px', color: '#6b7280', textAlign: 'center' }}>
                    +{plan.addons.seat}€/utilisateur • +{plan.addons.account}€/compte
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <style>{spinAnimation}</style>
        <Loader size={32} className="spin" style={{ color: '#1a1a1a' }} />
      </div>
    );
  }

  return (
    <div>
      <style>{spinAnimation}</style>

      {/* Header - même style que OrganizationView */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>
          <CreditCard size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Abonnement
        </h2>

        {/*
          * Pas de badge de formule ici : elle est nommée en grand dans la carte juste en
          * dessous, et une troisième fois sur sa carte « Plan actuel ». Trois affichages de la
          * même valeur obligent le lecteur à vérifier qu'ils concordent.
          */}
      </div>

      {/* Messages */}
      {error && <AlertMessage type="error" message={error} onClose={() => setError('')} />}
      {success && <AlertMessage type="success" message={success} onClose={() => setSuccess('')} />}

      {/* Contenu des onglets */}
      {/*
        * L'abonnement et les formules sur un seul écran.
        *
        * Ils étaient deux onglets : on voyait son plan d'un côté, on en changeait de l'autre,
        * et chacune des deux pages renvoyait à la suivante par un bouton. Or ces deux choses ne
        * se consultent jamais séparément — on vient ici pour comparer ce qu'on a avec ce qu'on
        * pourrait avoir. Les réunir supprime l'aller-retour et, surtout, met la formule actuelle
        * en regard des autres, ce qu'aucune des deux pages ne permettait.
        *
        * `plans` reste accepté comme valeur d'onglet : d'autres écrans y mènent encore
        * (`App.js` y redirige un compte sans abonnement valide), et une valeur inconnue
        * n'aurait rien affiché du tout.
        */}
      {(activeTab === 'subscription' || activeTab === 'plans') && renderSubscriptionTab()}
      {activeTab === 'invoices' && renderInvoicesTab()}
      {activeTab === 'payment' && renderPaymentTab()}
      {activeTab === 'billing' && renderBillingTab()}
    </div>
  );
}

export default SubscriptionPage;
